import { useState, useEffect } from 'react';
import api from '../lib/api';
import { AlertTriangle, RefreshCw, X } from 'lucide-react';

/**
 * Banner global affiché en haut quand le webhook Biborne est silencieux
 * pendant les heures d'ouverture. Auto-refresh toutes les 5 min.
 */
export default function BiborneHealthBanner({ full = false }) {
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const fetchStatus = async () => {
    try {
      const r = await api.get('/biborne/health/status');
      setStatus(r.data);
    } catch {
      // silent
    }
  };

  useEffect(() => {
    fetchStatus();
    const id = setInterval(fetchStatus, 15 * 60 * 1000); // 15 min (plus aucun appel live au cloud Biborne)
    return () => clearInterval(id);
  }, []);

  if (!full) return null;
  if (!status || dismissed) return null;

  const showSilent = status.is_silent;
  const showApiDown = status.api_status === 'error' && status.is_open_now;

  if (!showSilent && !showApiDown) return null;

  const handleResync = async () => {
    setRefreshing(true);
    try {
      const now = new Date();
      const month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      await api.post(`/biborne/resync-month?month=${month}`);
      await fetchStatus();
    } catch { /* ignore */ }
    finally { setRefreshing(false); }
  };

  const webhookUrl = `${process.env.REACT_APP_BACKEND_URL}/api/webhook/biborne`;
  const DIAG_MSG = {
    no_recent_requests: `La caisse n'appelle plus l'URL du webhook (dernier appel il y a ${status?.webhook_hit_minutes ?? '?'} min). Vérifie l'URL configurée côté Biborne : ${webhookUrl}`,
    never_received: `Aucune requête webhook jamais reçue. Configure cette URL côté Biborne : ${webhookUrl}`,
    rejected_signature: 'Des requêtes arrivent mais sont REJETÉES (signature HMAC invalide). Vérifie que le secret côté Biborne correspond à BIBORNE_WEBHOOK_SECRET.',
    hits_ok_but_silent: 'Des requêtes webhook arrivent mais aucune vente enregistrée récemment.',
  };

  return (
    <div className="sticky top-0 z-50 bg-gradient-to-r from-red-500 to-orange-500 text-white px-4 py-2.5 shadow-lg" data-testid="biborne-health-banner">
      <div className="max-w-6xl mx-auto flex items-center gap-3 flex-wrap">
        <AlertTriangle className="w-5 h-5 shrink-0 animate-pulse" />
        <div className="flex-1 min-w-0 text-xs md:text-sm">
          {showSilent && (
            <p className="font-bold">
              Webhook Biborne silencieux depuis <span className="underline">{status.silence_minutes} min</span> · API {status.api_status === 'ok' ? 'OK' : 'KO'}
            </p>
          )}
          {showSilent && status.webhook_diagnosis && (
            <p className="mt-0.5 font-medium break-all" data-testid="webhook-diagnosis">
              {DIAG_MSG[status.webhook_diagnosis] || ''}
            </p>
          )}
          {!showSilent && showApiDown && (
            <p className="font-bold">API Biborne indisponible (HTTP {status.api_http_code || '?'}). Les webhooks peuvent encore fonctionner.</p>
          )}
        </div>
        <button onClick={handleResync} disabled={refreshing} className="flex items-center gap-1 bg-surface-elev/20 hover:bg-surface-elev/30 px-3 py-1 rounded-lg text-xs font-bold disabled:opacity-50" data-testid="banner-resync-btn">
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} /> Resync
        </button>
        <button onClick={() => setDismissed(true)} className="p-1 hover:bg-surface-elev/20 rounded-lg" data-testid="banner-dismiss-btn">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
